import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import { PriceTagIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTravelCompanies } from '../../../api/travel-companies/get-travel-companies'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { getTripBookingGuidelines } from '../../../api/trip-booking-guidelines/get-trip-booking-guidelines'
import { getTripProviders } from '../../../api/trip-providers/get-trip-providers'
import { getUsers } from '../../../api/user/get-users'
import { Form, FormControl, FormField, FormItem } from '../../../components/elements/forms/form'
import ControlledInput from '../../../components/elements/inputs/controlled-input'
import ControlledSelectInput from '../../../components/elements/inputs/controlled-select-input'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { TransferInput } from '../../../lib/types/models/transfer/transfer-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import {
  createDateStringSchema,
  createTimeSchema
} from '../../../lib/utils/validations/date.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../elements/inputs/controlled-hour-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<NullableTransformer<TransferInput>> = z.object({
  lecResponsableId: createNumberSchema('responsable LEC', { nullish: true }),
  transportMeansId: createNumberSchema('moyen de transport'),
  reservationNumber: createStringSchema('numéro de réservation', { nullish: true }),
  numberOfStudents: createNumberSchema('nombre de jeunes'),
  numberOfStaff: createNumberSchema("nombre d'encadrants"),
  tripProviderId: createNumberSchema('prestataire voyage', { nullish: true }),
  totalPrice: createNumberSchema('totalPrice', { nullish: true }),
  travelCompany: createStringSchema('travelCompany', { nullish: true }),
  transferDate: createDateStringSchema({
    inputName: 'date de transfert'
  }),
  departureTime: createTimeSchema({
    inputName: 'heure de départ'
  }),
  arrivalTime: createTimeSchema({
    inputName: "heure d'arrivée"
  }),
  departureTimeLEC: createTimeSchema({
    inputName: 'heure de RDV LEC / AAT'
  }).nullish(),
  departureTimeProvider: createTimeSchema({
    inputName: 'heure de RDV prestataire'
  }).nullish(),
  departurePlaceId: createNumberSchema('lieu de départ'),
  arrivalPlaceId: createNumberSchema("lieu d'arrivée"),
  tripProviderGuidelineId: createNumberSchema('tripProviderGuidelineId', { optional: true }),
  tripProviderGuidelineContent: createStringSchema('tripProviderGuidelineContent', {
    nullish: true
  })
})

const TransferForm = ({
  defaultValues,
  className,
  ...validationButtonProps
}: BaseFormProps<TransferInput>) => {
  const form = useForm<TransferInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: tripProviders } = useQuery({
    queryKey: ['trip-providers'],
    queryFn: () => getTripProviders()
  })

  const { data: travelCompanies } = useQuery({
    queryKey: ['travel-companies'],
    queryFn: () => getTravelCompanies()
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const { data: tripBookingGuidelines } = useQuery({
    queryKey: ['trip-booking-guidelines'],
    queryFn: () => getTripBookingGuidelines()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={cn('p-4 rounded-2xl flex flex-col gap-3', className)}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Transfert
          </Typography>

          <FormField
            control={form.control}
            name={'lecResponsableId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    optional
                    searchable
                    label={'Responsable LEC'}
                    options={users?.data.map((user) => ({
                      label: toFullName(user.firstName, user.lastName),
                      value: user.id
                    }))}
                    selectedOption={getSelectedOption(
                      field.value,
                      toFullName(
                        users?.data.find((user) => user.id === field.value)?.firstName,
                        users?.data.find((user) => user.id === field.value)?.lastName
                      )
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'transportMeansId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    searchable
                    label={'Moyen de transport'}
                    options={transportMeans?.data.map((transportMean) => ({
                      label: getTranslatedName(transportMean),
                      value: transportMean.id
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label: getTranslatedName(
                        transportMeans?.data.find(
                          (transportMean) => transportMean.id === field.value
                        )
                      ),
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'transferDate'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    type="appointment"
                    label="Date de transfert"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'tripProviderId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      searchable
                      label={'Prestataire voyage'}
                      options={tripProviders?.data.map((tripProvider) => ({
                        label: tripProvider.companyName,
                        value: tripProvider.id
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        label:
                          tripProviders?.data.find(
                            (tripProvider) => tripProvider.id === field.value
                          )?.companyName ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'reservationNumber'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="text"
                      label={'Numéro de réservation'}
                      placeholder={'E.g. TR 1'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'numberOfStudents'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      type="number"
                      label={'Nombre de jeunes'}
                      placeholder={'E.g. 10'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'numberOfStaff'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      type="number"
                      label={"Nombre d'encadrants"}
                      placeholder={'E.g. 5'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'totalPrice'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      label={`Prix (€)`}
                      type={'number'}
                      icon={PriceTagIcon}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'travelCompany'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      searchable
                      allowNewOption
                      label={'Compagnie de voyage'}
                      options={travelCompanies?.data.map((company) => ({
                        label: company.name,
                        value: company.name
                      }))}
                      selectedOption={getSelectedOption(field.value, field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'departurePlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={'Lieu de départ'}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'departureTime'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput {...field} required label={'Heure de départ'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'arrivalPlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={"Lieu d'arrivée"}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'arrivalTime'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput {...field} required label={"Heure d'arrivée"} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'departureTimeLEC'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput {...field} label={'Heure de RDV LEC / AAT'} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'departureTimeProvider'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput {...field} label={'Heure de RDV prestataire'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-3 bg-white-hover p-4 rounded-lg border border-grey-light">
            <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
              Consigne à la compagnie
            </Typography>
            <FormField
              control={form.control}
              name={`tripProviderGuidelineId`}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      label={'Type de consigne'}
                      options={tripBookingGuidelines?.data.map((tripBookingGuideline) => ({
                        label: tripBookingGuideline.label,
                        value: tripBookingGuideline.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripBookingGuidelines?.data.find(
                            (tripBookingGuideline) => tripBookingGuideline.id === field.value
                          )?.label ?? '',
                        value: field.value
                      })}
                      onChange={(value) => {
                        field.onChange(value)
                        form.setValue(
                          'tripProviderGuidelineContent',
                          tripBookingGuidelines?.data.find(
                            (tripBookingGuideline) => tripBookingGuideline.id === value
                          )?.description ?? ''
                        )
                      }}
                      labelClassName="bg-white-hover"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'tripProviderGuidelineContent'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      disabled={form.watch('tripProviderGuidelineId') === undefined}
                      type={'textarea'}
                      label={'Consignes à la compagnie'}
                      labelClassName="bg-white-hover"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <ValidationButtons {...validationButtonProps} />
        </InnerBox>
      </form>
    </Form>
  )
}

export default TransferForm
